import {Percent} from 'lucide-react';

/**
 * Reference-data configurations used by <ReferenceManager />.
 * Each config describes the API endpoint, form fields, and display logic
 * for one type of reference entity.
 */

export const categoriesConfig = {
    queryKey: 'ref-categories',
    endpoint: '/categories',
    singularLabel: 'catégorie',
    pluralLabel: 'catégories',
    useFormData: true,
    searchParam: 'name',
    fields: [
        {key: 'name', label: 'Nom', type: 'text', required: true, placeholder: 'Nom de la catégorie', autoFocus: true},
        {key: 'description', label: 'Description', type: 'textarea', placeholder: 'Description (optionnelle)'},
        {key: 'color', label: 'Couleur', type: 'color', default: '#4DB6AC'},
        {key: 'active', label: 'Actif', type: 'toggle', default: true},
    ],
    renderSubtitle: (item) => item.description || null,
};

export const brandsConfig = {
    queryKey: 'ref-brands',
    endpoint: '/brands',
    singularLabel: 'marque',
    pluralLabel: 'marques',
    useFormData: true,
    searchParam: 'name',
    fields: [
        {key: 'name', label: 'Nom', type: 'text', required: true, placeholder: 'Nom de la marque', autoFocus: true},
        {key: 'description', label: 'Description', type: 'textarea', placeholder: 'Description (optionnelle)'},
        {key: 'color', label: 'Couleur', type: 'color', default: '#4DB6AC'},
        {key: 'active', label: 'Actif', type: 'toggle', default: true},
    ],
    renderSubtitle: (item) => item.description || null,
};

export const discountsConfig = {
    queryKey: 'ref-discounts',
    endpoint: '/discounts',
    singularLabel: 'remise',
    pluralLabel: 'remises',
    useFormData: true,
    searchParam: 'name',
    fields: [
        {key: 'name', label: 'Nom', type: 'text', required: true, placeholder: 'Nom de la remise', autoFocus: true},
        {key: 'value', label: 'Valeur (%)', type: 'number', required: true, placeholder: '10', min: 0, max: 100, step: 0.01},
        {key: 'description', label: 'Description', type: 'textarea', placeholder: 'Description (optionnelle)'},
        {key: 'color', label: 'Couleur', type: 'color', default: '#FF7043'},
        {key: 'active', label: 'Actif', type: 'toggle', default: true},
    ],
    renderSubtitle: (item) => item.value !== undefined ? `${item.value}%` : null,
    renderTags: (item) => item.value !== undefined ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400">
            <Percent className="w-3 h-3"/>
            {item.value}%
        </span>
    ) : null,
};

export const taxesConfig = {
    queryKey: 'ref-taxes',
    endpoint: '/taxes',
    singularLabel: 'taxe',
    pluralLabel: 'taxes',
    useFormData: false,
    searchParam: 'name',
    fields: [
        {key: 'name', label: 'Nom', type: 'text', required: true, placeholder: 'Ex: TVA 20%', autoFocus: true},
        {key: 'value', label: 'Taux (%)', type: 'number', required: true, placeholder: '20', min: 0, max: 100, step: 0.01},
        {key: 'active', label: 'Actif', type: 'toggle', default: true},
    ],
    renderAvatar: (item) => (
        <div className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold shadow-sm">
            {item.value !== undefined ? `${item.value}%` : '—'}
        </div>
    ),
    renderSubtitle: (item) => item.value !== undefined ? `Taux: ${item.value}%` : null,
    renderTags: (item) => item.value !== undefined ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
            <Percent className="w-3 h-3"/>
            {item.value}%
        </span>
    ) : null,
};

export const unitsConfig = {
    queryKey: 'ref-units',
    endpoint: '/units',
    singularLabel: 'unité',
    pluralLabel: 'unités',
    useFormData: false,
    searchParam: 'name',
    fields: [
        {key: 'name', label: 'Nom', type: 'text', required: true, placeholder: "Ex: Kilogramme", autoFocus: true},
        {key: 'code', label: 'Code', type: 'text', required: true, placeholder: 'Ex: kg'},
    ],
    renderAvatar: (item) => (
        <div className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-xs font-bold shadow-sm uppercase">
            {item.code || '—'}
        </div>
    ),
    renderSubtitle: (item) => item.code ? `Code: ${item.code}` : null,
};

/* ─────────────────────────────────────────────────────────────────────────────
   Calendar configs
   ───────────────────────────────────────────────────────────────────────────── */

/**
 * clientsConfig — used by the calendar EventModal to search, browse,
 * and create new clients inline without leaving the appointment form.
 */
export const clientsConfig = {
    queryKey:      'ref-clients',
    endpoint:      '/clients',
    singularLabel: 'un client',
    pluralLabel:   'clients',
    useFormData:   true,
    searchParam:   'name',
    /** Fields always appended to the POST payload but not shown in the create form */
    extraData: {
        device_name_created: 'web',
        device_name_updated: 'web',
        active:              'true',
        adresse:             '',
        birthday:            '2000-01-01',
        country_code:        '212',
    },
    /** How to derive a display name from a client object (API may use camelCase or snake_case) */
    getName: (item) =>
        `${item.firstName || item.first_name || ''} ${item.lastName || item.last_name || ''}`.trim() || '—',
    fields: [
        {key: 'first_name', label: 'Prénom',     type: 'text', required: true, placeholder: 'Prénom',           autoFocus: true},
        {key: 'last_name',  label: 'Nom',         type: 'text', required: true, placeholder: 'Nom de famille'},
        {key: 'phone',      label: 'Téléphone',   type: 'text', required: true, placeholder: '0612345678'},
        {key: 'email',      label: 'Email',        type: 'text',                placeholder: 'email@exemple.com'},
        {key: 'city',       label: 'Ville',        type: 'text',                placeholder: 'Casablanca'},
    ],
    renderAvatar: (item) => (
        <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
            style={{backgroundColor: '#4DB6AC'}}>
            {((item.firstName || item.first_name || '?').charAt(0)).toUpperCase()}
        </div>
    ),
    renderSubtitle: (item) => item.phone || null,
};

/**
 * servicesConfig — used by the calendar EventModal for multi-select service picking.
 * Inline creation is disabled because /products requires category + unit.
 * Users can create services from the catalogue instead.
 */
export const servicesConfig = {
    queryKey:      'ref-services',
    endpoint:      '/products',
    singularLabel: 'un service',
    pluralLabel:   'services',
    useFormData:   false,
    searchParam:   'name',
    disableCreate: true,   // hides the "Créer" button in the picker
    fields:        [],
    renderSubtitle: (item) => item.duration ? `${item.duration} min` : null,
};

/**
 * productsConfig — used to pick the parent product when converting a product
 * into a déclinaison (variant) of another product.
 */
export const productsConfig = {
    queryKey:      'ref-products',
    endpoint:      '/products',
    singularLabel: 'un produit',
    pluralLabel:   'produits',
    useFormData:   false,
    searchParam:   'name',
    // Lets the picker's search bar switch between filtering by name or by barcode.
    searchParams: [
        {key: 'name', label: 'Nom', placeholder: 'Rechercher par nom...'},
        {key: 'barcode', label: 'Code-barres', placeholder: 'Rechercher par code-barres...'},
    ],
    disableCreate: true,
    fields:        [],
    renderSubtitle: (item) => item.price !== undefined ? `${item.price} DH` : null,
};

/**
 * vendableProductsConfig / ingredientProductsConfig — used by TechnicalFileForm
 * to pick, respectively, the sellable product a recipe is attached to and the
 * ingredients that make it up. Same /products endpoint as productsConfig,
 * filtered server-side via isVendable/isIngredient so the picker only ever
 * shows products relevant to that field (and, being a real picker with search
 * + pagination, isn't capped at the API's default page size like a plain
 * unbounded <select> would be).
 */
export const vendableProductsConfig = {
    ...productsConfig,
    queryKey: 'ref-products-vendable',
    extraParams: {isVendable: true},
};

export const ingredientProductsConfig = {
    ...productsConfig,
    queryKey: 'ref-products-ingredient',
    extraParams: {isIngredient: true},
};

